import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Columns2 } from 'lucide-react'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'

import { QuoteLiteSchema, type Quote, type QuoteLite } from '@/gen/cortado/v1/invoices_pb'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { useQuoteLitesByIds } from '@/stores/quotes.store'
import { formatCurrency } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { QuoteStatusBadge } from '@/components/shared/enum-badge'
import { jobDetailSortQuotes, jobDetailWithTipText } from '@/components/pipeline/job-detail-view'
import { NewQuoteMenu } from '@/components/pipeline/new-quote-menu'
import { QuoteCompareSheet } from '@/components/pipeline/quote-compare-sheet'

interface QuoteSwitcherProps {
  jobId: string
  organizationId: string
  /** The inquiry's event type, for the New quote menu's matching template. */
  eventTypeKey?: string
  /** The job's quotes. */
  quoteIds: string[]
  acceptedQuoteId?: string
  /** The open quote; undefined while a new quote is being drafted. */
  currentQuote?: Quote
  /** False when the job has no inquiry to draft a new quote from. */
  canDraft: boolean
  /** Fired with the copy once the New quote menu copies a quote. */
  onDuplicated: (copy: Quote) => void
}

const SAMPLE_QUOTES: QuoteLite[] = [
  { title: 'Full service dinner', status: QuoteStatusEnum.QUOTE_STATUS_SENT },
  { title: 'Buffet option', status: QuoteStatusEnum.QUOTE_STATUS_DRAFT },
].map(({ title, status }, i) =>
  create(QuoteLiteSchema, { id: `sample-${i}`, quoteNumber: 1042 + i, title, totalCents: 485000n, status }),
)

/**
 * The job's quotes as a row of tabs above the quote editor, so a user can
 * move between alternatives without going back to the job. The accepted quote
 * is pinned first, then newest first (the job page's Quotes card order). The
 * trailing New quote menu starts another quote from a template, the request,
 * or a copy of one of these. Compare opens the open quote next to another one.
 */
export function QuoteSwitcher({
  jobId,
  organizationId,
  eventTypeKey,
  quoteIds,
  acceptedQuoteId,
  currentQuote,
  canDraft,
  onDuplicated,
}: QuoteSwitcherProps) {
  // A quote created or duplicated a moment ago may not be on the refreshed job yet.
  const ids = useMemo(
    () => (currentQuote && !quoteIds.includes(currentQuote.id) ? [...quoteIds, currentQuote.id] : quoteIds),
    [currentQuote, quoteIds],
  )
  const { quotes, isLoading } = useQuoteLitesByIds(ids)
  const isDrafting = !currentQuote

  const sorted = jobDetailSortQuotes(quotes, acceptedQuoteId)
  const [isComparing, setIsComparing] = useState(false)
  const compareWith = currentQuote ? sorted.filter((lite) => lite.id !== currentQuote.id) : []

  return (
    <div className="flex items-center gap-2">
      <nav aria-label="Quotes on this job" className="min-w-0 flex-1 overflow-x-auto">
        <Skeleton data={isLoading && sorted.length === 0 ? null : sorted} sample={SAMPLE_QUOTES} label="Loading quotes">
          {(sorted) => (
            <ul className="flex w-max items-stretch gap-1.5">
              {isDrafting && (
                <li>
                  <span
                    aria-current="page"
                    className="flex h-full items-center rounded-lg border border-brand bg-card px-3 py-1.5 text-sm font-medium"
                  >
                    New quote
                  </span>
                </li>
              )}
              {sorted.map((lite) => {
                const isCurrent = lite.id === currentQuote?.id
                // The open quote's own copy is fresher than the list's (unsaved title aside).
                const total = isCurrent ? currentQuote.totalCents : lite.totalCents
                const withTip = jobDetailWithTipText(total, isCurrent ? currentQuote.totalWithTipCents : lite.totalWithTipCents)
                const status = isCurrent ? currentQuote.status : lite.status
                return (
                  <li key={lite.id}>
                    <Link
                      to="/pipeline/$id/quotes/$quoteId"
                      params={{ id: jobId, quoteId: lite.id }}
                      aria-current={isCurrent ? 'page' : undefined}
                      className={cn(
                        'flex h-full max-w-64 flex-col justify-center gap-0.5 rounded-lg border px-3 py-1.5 text-left transition-colors',
                        isCurrent ? 'border-brand bg-card' : 'border-transparent bg-muted/50 hover:bg-muted',
                      )}
                    >
                      <span className="flex items-center gap-2 text-sm">
                        <span className="font-medium">#{lite.quoteNumber}</span>
                        {lite.id === acceptedQuoteId && <span className="text-xs text-muted-foreground">Accepted</span>}
                        <QuoteStatusBadge status={status} className="px-1.5 py-0 text-[10px]" />
                      </span>
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        {lite.title && <span className="min-w-0 truncate">{lite.title}</span>}
                        <span className="font-financial shrink-0" title={withTip}>
                          ${formatCurrency(Number(total))}
                          {withTip && <span className="sr-only"> ({withTip})</span>}
                        </span>
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </Skeleton>
      </nav>

      {currentQuote && compareWith.length > 0 && (
        <>
          <Button size="sm" variant="outline" className="shrink-0" onClick={() => setIsComparing(true)}>
            <Columns2 className="h-4 w-4" />
            Compare
          </Button>
          <QuoteCompareSheet
            open={isComparing}
            onOpenChange={setIsComparing}
            jobId={jobId}
            quoteId={currentQuote.id}
            quoteNumber={currentQuote.quoteNumber}
            others={compareWith}
            acceptedQuoteId={acceptedQuoteId}
          />
        </>
      )}

      <NewQuoteMenu
        jobId={jobId}
        organizationId={organizationId}
        eventTypeKey={eventTypeKey}
        quoteIds={ids}
        acceptedQuoteId={acceptedQuoteId}
        canDraft={canDraft}
        onDuplicated={onDuplicated}
      />
    </div>
  )
}
